/**
 * GenerationProgress — skeleton + "Generating…" hint shown
 * while the AI session-plan generation is in flight (issue
 * #437 acceptance criterion #9).
 *
 * Renders a faux plan skeleton (warm-up + activity cards +
 * prompts) so the SLP can see roughly what's coming, plus the
 * model name + an estimated seconds counter ("Generating…
 * ~15s").
 *
 * The estimated-seconds label is a one-shot countdown derived
 * from a typical 12-18s latency. The skeleton doesn't reflect
 * real progress — it's a UX cue while the network round-trip
 * completes (per the issue's risks note: real streaming is a
 * separate ticket).
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

export interface GenerationProgressProps {
  /** Model name to display (e.g. "claude-haiku-4-5"). */
  modelName?: string;
  /** Estimated seconds for the round-trip. Default 15. */
  estimatedSeconds?: number;
}

export function GenerationProgress({
  modelName,
  estimatedSeconds = 15,
}: GenerationProgressProps): ReactElement {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      setElapsed((n) => n + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const remaining = Math.max(0, estimatedSeconds - elapsed);
  return (
    <div className="generation-progress" data-testid="generation-progress">
      <p className="generation-progress-hint" data-testid="generation-progress-hint">
        Generating… ~{remaining}s{modelName ? ` · ${modelName}` : ''}
      </p>
      <div className="generation-progress-skeleton" aria-hidden="true">
        <div className="generation-progress-line generation-progress-line--h2" />
        <div className="generation-progress-line" />
        <div className="generation-progress-line" />
        <div className="generation-progress-line generation-progress-line--h2" />
        <div className="generation-progress-block">
          <div className="generation-progress-line generation-progress-line--h3" />
          <div className="generation-progress-line" />
          <div className="generation-progress-line" />
        </div>
        <div className="generation-progress-block">
          <div className="generation-progress-line generation-progress-line--h3" />
          <div className="generation-progress-line" />
        </div>
      </div>
    </div>
  );
}
